import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { nvStorage } from '@simwood/nv-react-components';
import { finaliseLogout } from '../../shared/store/thunks';
import appConfig from '../helpers/appConfig';
import {
  setInboundRingingVolume as applyInboundRingingVolume,
  setOutputVolume,
} from '../helpers/ringing';

interface AudioState {
  volume: number;
  inboundRingingVolume: number;
  selectedAudioInputDeviceId: string;
  selectedAudioOutputDeviceId: string;
  selectedRingingOutputDeviceId: string;
  hasGrantedAudio: boolean;
  lastOpenPreferencesRequest?: number;
  error?: SerializedError;
}

const initialState: AudioState = {
  volume: Number(nvStorage.get('volume') ?? 1),
  inboundRingingVolume: Number(nvStorage.get('inboundRingingVolume') ?? 1),
  selectedAudioInputDeviceId:
    (nvStorage.get('selectedAudioInputDeviceId') as string) || 'default',
  selectedAudioOutputDeviceId:
    (nvStorage.get('selectedAudioOutputDeviceId') as string) || 'default',
  selectedRingingOutputDeviceId:
    (nvStorage.get('selectedRingingOutputDeviceId') as string) || 'default',
  hasGrantedAudio: false,
  lastOpenPreferencesRequest: undefined,
  error: undefined,
};

const audioSlice = createSlice({
  name: 'audio',
  initialState,
  reducers: {
    setInboundRingingVolume: (state, action: PayloadAction<number>) => {
      nvStorage.set('inboundRingingVolume', action.payload);
      applyInboundRingingVolume(action.payload);
      state.inboundRingingVolume = action.payload;
    },
    setVolume: (state, action: PayloadAction<number>) => {
      const { extension } = appConfig;

      nvStorage.set('volume', action.payload);
      extension?.setVolume(action.payload);
      setOutputVolume(action.payload);
      state.volume = action.payload;
    },
    selectAudioInputDevice: (state, action: PayloadAction<string>) => {
      nvStorage.set('selectedAudioInputDeviceId', action.payload);
      state.selectedAudioInputDeviceId = action.payload;
    },
    selectAudioOutputDevice: (state, action: PayloadAction<string>) => {
      nvStorage.set('selectedAudioOutputDeviceId', action.payload);
      state.selectedAudioOutputDeviceId = action.payload;
    },
    selectRingingOutputDevice: (state, action: PayloadAction<string>) => {
      nvStorage.set('selectedRingingOutputDeviceId', action.payload);
      state.selectedRingingOutputDeviceId = action.payload;
    },
    grantAudio: (state, action: PayloadAction<boolean>) => {
      state.hasGrantedAudio = action.payload;
    },
    openPreferences: (state) => {
      state.lastOpenPreferencesRequest = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(finaliseLogout.fulfilled, () => initialState);
  },
});

export const {
  setInboundRingingVolume,
  setVolume,
  selectAudioInputDevice,
  selectAudioOutputDevice,
  selectRingingOutputDevice,
  grantAudio,
  openPreferences,
} = audioSlice.actions;

export default audioSlice.reducer;
